import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Input } from 'components/Input/Input'
import { ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { OtherText, TitleText } from 'styling/Typography'
import { IOpsRequest, OpsRequestStatus } from 'types/opsRequests.interface'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { IOpsRequestsSlaSummary } from 'types/opsToday.interface'
import {
  acknowledgeOpsRequest,
  completeOpsRequest,
  declineOpsRequest,
  startOpsRequest,
} from 'features/operations/opsRequestsSlice'
import { deptName } from '../Shared/opsDepartments'
import {
  REQUEST_PRIORITY_META,
  REQUEST_STATUS_META,
  SLA_TONE,
  requestSlaLabel,
  requestSlaState,
} from '../Shared/opsRequestsData'
import {
  DeclineFieldWrap,
  DeclineInlineWrap,
  HandledGlyph,
  InboundRequestsContainer,
  OutboundLine,
  OutboundList,
  OutboundRequestsContainer,
  OutboundRow,
  OutboundToggleHeader,
  RequestActionsWrap,
  RequestCode,
  RequestHead,
  RequestList,
  RequestMeta,
  RequestPillsRow,
  RequestRow,
  RequestTextWrap,
  RequestTitleText,
  SummaryLabel,
  SummaryRow,
} from './OpsToday.styled'

const ACTIVE_STATUSES: OpsRequestStatus[] = ['open', 'acknowledged', 'in_progress']

const isActive = (r: IOpsRequest) => ACTIVE_STATUSES.includes(r.status)

export const RequestsToDept = ({ dept }: { dept: IOpsDepartment }) => {
  const [decliningId, setDecliningId] = useState<number | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const requests = useSelector((state: RootState) => state.opsRequests.requests)
  const inbound = requests.filter((r) => r.toDept === dept.key && isActive(r))

  const handleAcknowledge = (r: IOpsRequest) => {
    dispatch(acknowledgeOpsRequest({ id: r.id }))
    toast.success(`Acknowledged — ${deptName(r.fromDept)} can see you're on it`)
  }

  const handleStart = (r: IOpsRequest) => {
    dispatch(startOpsRequest({ id: r.id }))
    toast.success(`Started — ${deptName(r.fromDept)} can see work is underway`)
  }

  const handleComplete = (r: IOpsRequest) => {
    dispatch(completeOpsRequest({ id: r.id }))
    toast.success(`Done — ${deptName(r.fromDept)} has been told`)
  }

  const openDecline = (r: IOpsRequest) => {
    setDecliningId(r.id)
    setDeclineReason('')
  }

  const confirmDecline = (r: IOpsRequest) => {
    dispatch(declineOpsRequest({ id: r.id, reason: declineReason.trim() }))
    toast.success(`Declined — ${deptName(r.fromDept)} will see your reason`)
    setDecliningId(null)
    setDeclineReason('')
  }

  return (
    <InboundRequestsContainer>
      <ContainerHeader>
        <TitleText>Requests to {dept.name}</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {inbound.length === 0 ? (
          <OtherText grey>No requests to {dept.name} right now.</OtherText>
        ) : (
          <RequestList>
            {inbound.map((r) => (
              <RequestRow key={r.id}>
                <RequestTextWrap>
                  <RequestHead>
                    <RequestCode>{r.code}</RequestCode>
                    <RequestTitleText>{r.title}</RequestTitleText>
                  </RequestHead>
                  <RequestMeta>
                    From {deptName(r.fromDept)} · {r.createdBy} · {r.createdAt}
                  </RequestMeta>
                  <RequestPillsRow>
                    <StatusPill status={REQUEST_PRIORITY_META[r.priority].tone}>
                      {REQUEST_PRIORITY_META[r.priority].label}
                    </StatusPill>
                    <StatusPill status={SLA_TONE[requestSlaState(r)]}>{requestSlaLabel(r)}</StatusPill>
                    {r.escalationLevel > 0 && <StatusPill status='danger'>Escalated</StatusPill>}
                    <StatusPill status={REQUEST_STATUS_META[r.status].tone}>
                      {REQUEST_STATUS_META[r.status].label}
                    </StatusPill>
                  </RequestPillsRow>
                </RequestTextWrap>
                <RequestActionsWrap>
                  {r.status === 'open' && decliningId !== r.id && (
                    <>
                      <Button size='sm' variant='primary' onClick={() => handleAcknowledge(r)}>
                        Acknowledge
                      </Button>
                      <Button size='sm' variant='black' onClick={() => openDecline(r)}>
                        Decline
                      </Button>
                    </>
                  )}
                  {r.status === 'acknowledged' && (
                    <Button size='sm' variant='primary' onClick={() => handleStart(r)}>
                      Start work
                    </Button>
                  )}
                  {r.status === 'in_progress' && (
                    <Button size='sm' variant='success' onClick={() => handleComplete(r)}>
                      Mark done
                    </Button>
                  )}
                  {r.linkedPath && (
                    <Button size='sm' variant='black' onClick={() => navigate(r.linkedPath as string)}>
                      Open linked item
                    </Button>
                  )}
                </RequestActionsWrap>
                {decliningId === r.id && (
                  <DeclineInlineWrap>
                    <DeclineFieldWrap>
                      <Input
                        nomarg
                        placeholder='Tell them why — required'
                        value={declineReason}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeclineReason(e.target.value)}
                      />
                    </DeclineFieldWrap>
                    <Button
                      size='sm'
                      variant='danger'
                      disabled={!declineReason.trim()}
                      onClick={() => confirmDecline(r)}
                    >
                      Confirm decline
                    </Button>
                    <Button size='sm' variant='black' onClick={() => setDecliningId(null)}>
                      Cancel
                    </Button>
                  </DeclineInlineWrap>
                )}
              </RequestRow>
            ))}
          </RequestList>
        )}
      </ContainerContent>
    </InboundRequestsContainer>
  )
}

export const RequestsSentByDept = ({ dept }: { dept: IOpsDepartment }) => {
  const [open, setOpen] = useState(false)
  const requests = useSelector((state: RootState) => state.opsRequests.requests)
  const sent = requests.filter((r) => r.fromDept === dept.key)

  if (sent.length === 0) return null

  return (
    <OutboundRequestsContainer>
      <OutboundToggleHeader $open={open} onClick={() => setOpen((prev) => !prev)}>
        <TitleText>
          Sent by {dept.name} ({sent.length})
        </TitleText>
        <HandledGlyph $open={open}>▾</HandledGlyph>
      </OutboundToggleHeader>
      {open && (
        <ContainerContent>
          <OutboundList>
            {sent.map((r) => {
              const muted = r.status === 'done' || r.status === 'declined'
              const acknowledgedPart = r.acknowledgedAt ? ` · acknowledged ${r.acknowledgedAt}` : ''
              const declinedPart = r.declineReason ? ` · declined: ${r.declineReason}` : ''
              const line = `${r.code} · ${r.title} — sent to ${deptName(r.toDept)} · ${REQUEST_STATUS_META[r.status].label
                }${acknowledgedPart}${declinedPart}`
              return (
                <OutboundRow key={r.id} $muted={muted}>
                  <OutboundLine>{line}</OutboundLine>
                  <RequestPillsRow>
                    <StatusPill status={REQUEST_STATUS_META[r.status].tone}>
                      {REQUEST_STATUS_META[r.status].label}
                    </StatusPill>
                    <StatusPill status={SLA_TONE[requestSlaState(r)]}>{requestSlaLabel(r)}</StatusPill>
                  </RequestPillsRow>
                </OutboundRow>
              )
            })}
          </OutboundList>
        </ContainerContent>
      )}
    </OutboundRequestsContainer>
  )
}

export const SiteRequestsSummary = () => {
  const requests = useSelector((state: RootState) => state.opsRequests.requests)
  const active = requests.filter(isActive)

  if (active.length === 0) return null

  const summary: IOpsRequestsSlaSummary = { overdue: 0, dueSoon: 0, onTime: 0 }
  active.forEach((r) => {
    const state = requestSlaState(r)
    if (state === 'overdue') summary.overdue += 1
    else if (state === 'due_soon') summary.dueSoon += 1
    else summary.onTime += 1
  })

  return (
    <SummaryRow>
      <SummaryLabel>Open cross-department requests ({active.length})</SummaryLabel>
      {summary.overdue > 0 && <StatusPill status='danger'>{summary.overdue} overdue</StatusPill>}
      {summary.dueSoon > 0 && <StatusPill status='warning'>{summary.dueSoon} due soon</StatusPill>}
      {summary.onTime > 0 && <StatusPill status='info'>{summary.onTime} on time</StatusPill>}
    </SummaryRow>
  )
}
